import { styleProfiles, type StyleCategory, type StyleGeometry } from '@workspace/turban-styles';

export type FaceShape = 'oval' | 'round' | 'square' | 'long';
export type Occasion = 'everyday' | 'wedding' | 'work' | 'evening' | 'religious' | 'festival';
export type TurbanGeometry = StyleGeometry;

export type TurbanStyle = {
  id: string;
  name: string;
  category: StyleCategory;
  region: string;
  description: string;
  bestFor: string;
  silhouette: string;
  visualSpec: string;
  tags: string[];
  colors: string[];
  faceShapes: FaceShape[];
  occasions: Occasion[];
  accent: string;
  art: string;
  image: string;
  colorImages: Record<string, string>;
  geometry: TurbanGeometry;
};

const styleDisplayProfiles: Omit<TurbanStyle, 'colorImages'>[] = [
  {
    id: 'patiala-shahi',
    name: 'Patiala Shahi',
    category: 'Punjabi Styles',
    region: 'Majha · Statement drape',
    description: 'A generous, regal silhouette with a little more height and plenty of presence.',
    bestFor: 'When you want your dastaar to lead the look.',
    silhouette: 'Full rounded crown, broad V front',
    visualSpec: 'Authentic, proportionate Patiala Shahi with a full rounded crown, a broad symmetrical V-shaped front, and crisp diagonal larhs visible on both sides. The top must remain smoothly and continuously wrapped. Never add an upright top flare, fan, rosette, plume, crest, loose tail, or flower-like fabric projection. Keep natural layered volume and allow the lower ear lobes to remain slightly visible.',
    tags: ['Regal', 'Statement', 'Classic'],
    colors: ['saffron', 'wine', 'ink'],
    faceShapes: ['round', 'square', 'oval'],
    occasions: ['wedding', 'evening', 'work', 'festival'],
    accent: '#8d2941',
    art: '#7f263b',
    image: '/style-images/normal-patiala-shahi.jpg',
    geometry: { frontGeometry: 'broad fan with layered centre', foldDirection: 'diagonal right', crownHeight: 'high', foreheadPosition: 'open' },
  },
  {
    id: 'amritsari',
    name: 'Amritsari',
    category: 'Punjabi Styles',
    region: 'Amritsar · Clean pleats',
    description: 'Neat, balanced pleats with an easy everyday polish. Quietly sharp, never overworked.',
    bestFor: 'The office, campus, or any day you want to feel put together.',
    silhouette: 'Medium crown, crisp fan',
    visualSpec: 'A balanced everyday Amritsari with neat parallel pleats and a small centred fan.',
    tags: ['Everyday', 'Polished', 'Balanced'],
    colors: ['sage', 'cream', 'charcoal'],
    faceShapes: ['round', 'oval', 'square'],
    occasions: ['everyday', 'work', 'religious'],
    accent: '#35656a',
    art: '#2b5960',
    image: '/style-images/normal-amritsari.jpg',
    geometry: { frontGeometry: 'compact centred fan', foldDirection: 'diagonal left', crownHeight: 'medium', foreheadPosition: 'balanced' },
  },
  {
    id: 'morni',
    name: 'Morni',
    category: 'Punjabi Styles',
    region: 'Punjab · Tapered crown',
    description: 'A lifted, feathered silhouette with an elegant sweep that gives the face a longer line.',
    bestFor: 'Festivals, dinners, and a confident Punjabi statement.',
    silhouette: 'Tapered crown, feathered front',
    visualSpec: 'A tapered crown with a sculpted feather-like front sweep and measured layered pleats.',
    tags: ['Expressive', 'Lifted', 'Festival'],
    colors: ['cobalt', 'saffron', 'ink'],
    faceShapes: ['round', 'oval', 'square'],
    occasions: ['festival', 'evening', 'wedding'],
    accent: '#2d5b78',
    art: '#234963',
    image: '',
    geometry: { frontGeometry: 'feathered central sweep', foldDirection: 'diagonal right', crownHeight: 'medium-high', foreheadPosition: 'open' },
  },
  {
    id: 'nok',
    name: 'Nok',
    category: 'Punjabi Styles',
    region: 'Punjab · Defined peak',
    description: 'A clean pointed finish with disciplined pleats and a composed, directional profile.',
    bestFor: 'Formal days when a sharper outline feels right.',
    silhouette: 'Defined peak, close pleats',
    visualSpec: 'A tidy pointed front with close diagonal pleats and a restrained crown.',
    tags: ['Sharp', 'Structured', 'Formal'],
    colors: ['wine', 'cream', 'ink'],
    faceShapes: ['round', 'oval', 'long'],
    occasions: ['work', 'evening', 'wedding'],
    accent: '#80354b',
    art: '#62273b',
    image: '',
    geometry: { frontGeometry: 'narrow pointed peak', foldDirection: 'diagonal left', crownHeight: 'medium-high', foreheadPosition: 'balanced' },
  },
  {
    id: 'malwai',
    name: 'Malwai',
    category: 'Punjabi Styles',
    region: 'Malwa · Easy volume',
    description: 'A generous regional wrap with soft volume and a relaxed, handsome finish.',
    bestFor: 'Family gatherings, Vaisakhi, and long days outdoors.',
    silhouette: 'Soft volume, rounded front',
    visualSpec: 'A softly rounded crown with broad relaxed pleats that keep natural fabric volume.',
    tags: ['Regional', 'Relaxed', 'Full'],
    colors: ['terracotta', 'oat', 'navy'],
    faceShapes: ['square', 'oval', 'long'],
    occasions: ['everyday', 'festival', 'religious'],
    accent: '#a85d42',
    art: '#804431',
    image: '',
    geometry: { frontGeometry: 'wide rounded front', foldDirection: 'horizontal', crownHeight: 'medium-high', foreheadPosition: 'open' },
  },
  {
    id: 'barnala',
    name: 'Barnala',
    category: 'Punjabi Styles',
    region: 'Barnala · Neat drape',
    description: 'A measured, low-friction style: clean lines, a low crown, and a polished regional character.',
    bestFor: 'Work, college, and everyday dressing with intention.',
    silhouette: 'Low crown, clean fan',
    visualSpec: 'A compact low crown with clean diagonal folds and a small open forehead line.',
    tags: ['Neat', 'Daily', 'Measured'],
    colors: ['moss', 'sand', 'ink'],
    faceShapes: ['round', 'oval', 'square'],
    occasions: ['everyday', 'work', 'religious'],
    accent: '#5a7667',
    art: '#40594e',
    image: '',
    geometry: { frontGeometry: 'small centred fan', foldDirection: 'diagonal right', crownHeight: 'low', foreheadPosition: 'balanced' },
  },
  {
    id: 'dumalla',
    name: 'Dumalla',
    category: 'Traditional Sikh',
    region: 'Nihang tradition · Layered',
    description: 'Architectural layers and a proud peak, made for a look that carries history forward.',
    bestFor: 'Ceremonies, gatherings, and days with something to say.',
    silhouette: 'Modest peak, layered wrap',
    visualSpec: 'Normal, wearable Dumalla with a modest rounded peak and compact stacked horizontal layers; practical circular wraps at natural head scale; no broad Patiala fan and never an extreme tall crown.',
    tags: ['Heritage', 'Bold', 'Layered'],
    colors: ['navy', 'electric blue', 'saffron'],
    faceShapes: ['oval', 'long', 'square'],
    occasions: ['wedding', 'evening', 'religious', 'festival'],
    accent: '#1d456b',
    art: '#163d5c',
    image: '/style-images/normal-dumalla.jpg',
    geometry: { frontGeometry: 'stacked layered peak', foldDirection: 'horizontal', crownHeight: 'medium-high', foreheadPosition: 'open' },
  },
  {
    id: 'gol-dastar', name: 'Gol Dastar', category: 'Traditional Sikh', region: 'Sikh tradition · Round crown', description: 'A centred, circular silhouette with calm symmetry and strong presence.', bestFor: 'Darbar, seva, and heritage-led dressing.', silhouette: 'Rounded crown, even wraps', visualSpec: 'A balanced circular crown built from even horizontal wraps.', tags: ['Heritage', 'Symmetrical', 'Calm'], colors: ['navy', 'cream', 'saffron'], faceShapes: ['oval', 'square', 'long'], occasions: ['religious', 'festival'], accent: '#345679', art: '#27445f', image: '', geometry: { frontGeometry: 'circular centred front', foldDirection: 'horizontal', crownHeight: 'medium', foreheadPosition: 'balanced' },
  },
  {
    id: 'keski', name: 'Keski', category: 'Traditional Sikh', region: 'Sikh tradition · Compact', description: 'A compact, practical dastaar with a quietly grounded silhouette.', bestFor: 'Daily practice, travel, and understated tradition.', silhouette: 'Compact crown, close pleats', visualSpec: 'A compact wrap with close diagonal folds and a clear, comfortable forehead line.', tags: ['Practical', 'Grounded', 'Daily'], colors: ['charcoal', 'oat', 'cobalt'], faceShapes: ['oval', 'round', 'square'], occasions: ['everyday', 'religious', 'work'], accent: '#5c6370', art: '#414752', image: '', geometry: { frontGeometry: 'compact angled front', foldDirection: 'diagonal left', crownHeight: 'low', foreheadPosition: 'balanced' },
  },
  {
    id: 'punjabi-groom', name: 'Punjabi Groom', category: 'Wedding', region: 'Wedding edit · Punjabi', description: 'A celebratory, broad-front silhouette made for the groom at the centre of the room.', bestFor: 'Anand Karaj, milni, and baraat portraits.', silhouette: 'Broad fan, lifted crown', visualSpec: 'A broad structured fan with a lifted crown and room for a subtle kalgi placement.', tags: ['Groom', 'Celebratory', 'Punjabi'], colors: ['saffron', 'wine', 'cream'], faceShapes: ['oval', 'round', 'square'], occasions: ['wedding', 'festival'], accent: '#a66b35', art: '#7d4e28', image: '', geometry: { frontGeometry: 'broad formal fan', foldDirection: 'diagonal right', crownHeight: 'high', foreheadPosition: 'open' },
  },
  {
    id: 'royal-groom', name: 'Royal Groom', category: 'Wedding', region: 'Wedding edit · Regal', description: 'A taller, sculpted silhouette with ceremony-ready scale and restraint.', bestFor: 'Formal wedding portraits and a full royal sherwani.', silhouette: 'Tall crown, sculpted fan', visualSpec: 'A tall sculpted crown with layered diagonal folds and a clean open forehead.', tags: ['Royal', 'Sculpted', 'Formal'], colors: ['deep maroon', 'gold', 'ink'], faceShapes: ['oval', 'long', 'square'], occasions: ['wedding', 'evening'], accent: '#743d48', art: '#512b35', image: '', geometry: { frontGeometry: 'sculpted stepped fan', foldDirection: 'diagonal left', crownHeight: 'high', foreheadPosition: 'open' },
  },
  {
    id: 'classic-wedding', name: 'Classic Wedding', category: 'Wedding', region: 'Wedding edit · Timeless', description: 'A balanced formal wrap that lets the outfit and the face share the spotlight.', bestFor: 'Ceremonies where timeless beats theatrical.', silhouette: 'Balanced crown, clean pleats', visualSpec: 'Medium-high crown with balanced pleats and a formal centred fan.', tags: ['Timeless', 'Balanced', 'Ceremony'], colors: ['cream', 'wine', 'navy'], faceShapes: ['round', 'oval', 'square'], occasions: ['wedding', 'religious'], accent: '#92725c', art: '#6f5547', image: '', geometry: { frontGeometry: 'formal centred fan', foldDirection: 'diagonal right', crownHeight: 'medium-high', foreheadPosition: 'balanced' },
  },
  {
    id: 'modern-wedding', name: 'Modern Wedding', category: 'Wedding', region: 'Wedding edit · Contemporary', description: 'A cleaner wedding line with less bulk, crisp edges, and contemporary confidence.', bestFor: 'Minimal sherwanis, receptions, and editorial portraits.', silhouette: 'Clean crown, sharp fold', visualSpec: 'A medium clean crown with crisp directional pleats and a restrained front line.', tags: ['Modern', 'Clean', 'Editorial'], colors: ['charcoal', 'cream', 'rust'], faceShapes: ['long', 'oval'], occasions: ['wedding', 'evening'], accent: '#59626a', art: '#3d464d', image: '', geometry: { frontGeometry: 'slim angular fan', foldDirection: 'diagonal left', crownHeight: 'medium', foreheadPosition: 'open' },
  },
  {
    id: 'parna', name: 'Parna', category: 'Casual', region: 'Everyday · Light wrap', description: 'A lightweight wrap with an easy forehead line and unforced movement.', bestFor: 'Errands, summer days, and relaxed family plans.', silhouette: 'Low crown, loose drape', visualSpec: 'A low, soft crown with loose diagonal fabric folds and a natural open forehead.', tags: ['Light', 'Relaxed', 'Easy'], colors: ['oat', 'moss', 'rust'], faceShapes: ['oval', 'long', 'round'], occasions: ['everyday', 'festival'], accent: '#9a785d', art: '#735942', image: '', geometry: { frontGeometry: 'soft loose front', foldDirection: 'diagonal right', crownHeight: 'low', foreheadPosition: 'open' },
  },
  {
    id: 'wattan-wali-pagg', name: 'Wattan Wali Pagg', category: 'Casual', region: 'Everyday · Punjabi', description: 'A familiar, softly full wrap with the lived-in character of a favourite everyday pagg.', bestFor: 'Family visits, neighbourhood walks, and daily wear.', silhouette: 'Soft full crown, broad pleats', visualSpec: 'A softly full crown with broad relaxed diagonal pleats and an even front.', tags: ['Familiar', 'Soft', 'Daily'], colors: ['denim', 'cream', 'terracotta'], faceShapes: ['round', 'oval', 'square'], occasions: ['everyday', 'work', 'religious'], accent: '#58778a', art: '#3e596b', image: '', geometry: { frontGeometry: 'soft broad fan', foldDirection: 'diagonal left', crownHeight: 'medium', foreheadPosition: 'balanced' },
  },
  {
    id: 'double-patti', name: 'Double Patti', category: 'Casual', region: 'Everyday · Layered', description: 'Two visible bands create a tidy graphic rhythm without adding unnecessary height.', bestFor: 'College, work, and smart casual dressing.', silhouette: 'Medium crown, double band', visualSpec: 'Two clear horizontal bands over a compact crown, finished with narrow diagonal pleats.', tags: ['Graphic', 'Layered', 'Smart'], colors: ['ink', 'saffron', 'cream'], faceShapes: ['oval', 'square', 'long'], occasions: ['everyday', 'work'], accent: '#425975', art: '#2d4057', image: '', geometry: { frontGeometry: 'double horizontal band', foldDirection: 'horizontal', crownHeight: 'medium', foreheadPosition: 'balanced' },
  },
  {
    id: 'uk-style', name: 'UK Style', category: 'Casual', region: 'Contemporary · Clean', description: 'A crisp diaspora silhouette with a low profile and quietly tailored energy.', bestFor: 'City days, workwear, and modern street style.', silhouette: 'Low profile, crisp folds', visualSpec: 'A low profile with crisp parallel folds and an uncluttered forehead line.', tags: ['Diaspora', 'Crisp', 'Modern'], colors: ['charcoal', 'cobalt', 'cream'], faceShapes: ['long', 'oval'], occasions: ['everyday', 'work', 'evening'], accent: '#5c6872', art: '#3d4750', image: '', geometry: { frontGeometry: 'slim parallel front', foldDirection: 'diagonal right', crownHeight: 'low', foreheadPosition: 'open' },
  },
  {
    id: 'kenyan-style', name: 'Kenyan Style', category: 'Casual', region: 'Contemporary · East Africa', description: 'A practical, expressive wrap shaped by warm-weather dressing and diaspora style.', bestFor: 'Warm climates, community gatherings, and everyday colour.', silhouette: 'Rounded crown, expressive folds', visualSpec: 'A rounded breathable crown with expressive diagonal folds and a comfortable open forehead.', tags: ['Diaspora', 'Warm-weather', 'Expressive'], colors: ['terracotta', 'cobalt', 'oat'], faceShapes: ['round', 'oval', 'square'], occasions: ['everyday', 'festival', 'religious'], accent: '#a75d46', art: '#7c4435', image: '', geometry: { frontGeometry: 'rounded expressive front', foldDirection: 'diagonal left', crownHeight: 'medium', foreheadPosition: 'open' },
  },
];

const styleDisplayById = new Map(styleDisplayProfiles.map((style) => [style.id, style]));

export const styleColorHex: Record<string, string> = {
  ink: '#172337', saffron: '#d9a441', wine: '#8d2941', cream: '#eee1c7',
  sage: '#7c9c89', lavender: '#9d88ad', cobalt: '#47718e', terracotta: '#a95037',
  black: '#111111', white: '#ffffff', grey: '#888888', navy: '#1a2b4c',
  charcoal: '#444950', oat: '#c9b79c', moss: '#64735b', sand: '#c9aa7d',
  'electric blue': '#246be8', 'deep maroon': '#6f2037', gold: '#c79a4c',
  rust: '#a84f35', denim: '#58778a',
};

function colourAssetSlug(colour: string) {
  return colour.toLowerCase().replaceAll(' ', '-');
}

function createStylePlaceholder(name: string, accent: string, art: string, colourName?: string) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 1000" role="img" aria-label="${name} fabric fold study">
    <defs>
      <linearGradient id="base" x1="0" y1="0" x2="1" y2="1"><stop stop-color="${accent}"/><stop offset="1" stop-color="${art}"/></linearGradient>
      <linearGradient id="fold" x1="0" y1="0" x2="0" y2="1"><stop stop-color="#fff" stop-opacity=".3"/><stop offset=".55" stop-color="#fff" stop-opacity=".04"/><stop offset="1" stop-color="#000" stop-opacity=".24"/></linearGradient>
    </defs>
    <rect width="800" height="1000" fill="url(#base)"/>
    <path d="M-80 880C120 480 260 240 410-40L560-40C390 290 260 560 90 1040Z" fill="url(#fold)"/>
    <path d="M150 1040C340 610 475 300 610-40H735C620 330 500 700 390 1040Z" fill="url(#fold)" opacity=".78"/>
    <path d="M430 1040C560 670 690 330 880 40V1040Z" fill="url(#fold)" opacity=".58"/>
    <circle cx="650" cy="155" r="190" fill="#fff" opacity=".05"/>
    ${colourName ? `<rect x="34" y="900" width="732" height="58" rx="29" fill="#101c2d" opacity=".72"/><text x="400" y="938" text-anchor="middle" fill="white" font-family="sans-serif" font-size="25" letter-spacing="4">${colourName.toUpperCase()}</text>` : ''}
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export const styles: TurbanStyle[] = styleProfiles.map((profile) => {
  const display = styleDisplayById.get(profile.id);
  if (!display) throw new Error(`Missing web display profile for ${profile.id}`);
  return {
    ...display,
    id: profile.id,
    name: profile.name,
    category: profile.category,
    visualSpec: profile.constructionPrompt,
    geometry: profile.geometry,
    image: `/style-images/authentic/${profile.id}-${colourAssetSlug(display.colors[0])}.jpg`,
    colorImages: Object.fromEntries(display.colors.map((colour) => [
      colour,
      `/style-images/authentic/${profile.id}-${colourAssetSlug(colour)}.jpg`,
    ])),
  };
});

export const faceOptions: { value: FaceShape; label: string; note: string }[] = [
  { value: 'oval', label: 'Oval', note: 'Balanced & versatile' },
  { value: 'round', label: 'Round', note: 'Soft & youthful' },
  { value: 'square', label: 'Square', note: 'Strong jawline' },
  { value: 'long', label: 'Long', note: 'More length than width' },
];

export const occasionOptions: { value: Occasion; label: string; note: string }[] = [
  { value: 'everyday', label: 'Everyday', note: 'Class, errands, family' },
  { value: 'wedding', label: 'Wedding', note: 'Anand Karaj or baraat' },
  { value: 'work', label: 'Work or college', note: 'Sharp, considered, easy' },
  { value: 'evening', label: 'Dinner or party', note: 'Going out, late night' },
  { value: 'religious', label: 'Gurudwara', note: 'Seva, darbar, gathering' },
  { value: 'festival', label: 'Festival or event', note: 'Lohri, Vaisakhi, celebrations' },
];

export const outfitColors = [
  { value: 'neutrals', label: 'Neutrals', color: '#d2c4ae' },
  { value: 'denim', label: 'Denim blue', color: '#47718e' },
  { value: 'earth', label: 'Earth tones', color: '#9c6947' },
  { value: 'black', label: 'Black', color: '#191b20' },
  { value: 'cream', label: 'Cream', color: '#eee1c7' },
  { value: 'colour', label: 'Statement red', color: '#b34b55' },
];

export function getRecommendation(face: FaceShape, occasion: Occasion, outfit: string) {
  const ranked = styles.map((style) => {
    let score = 72;
    if (style.faceShapes.includes(face)) score += 12;
    if (style.occasions.includes(occasion)) score += 10;
    if (outfit === 'black' && style.id === 'dumalla') score += 4;
    if (outfit === 'denim' && style.id === 'mona') score += 5;
    if (outfit === 'cream' && style.id === 'patiala-shahi') score += 4;
    if (outfit === 'earth' && style.id === 'gol-pagg') score += 3;
    return { style, score };
  });
  return ranked.sort((a, b) => b.score - a.score)[0];
}